import {
  createFrontendModule,
  PluginHeaderActionBlueprint,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import { ButtonLink, Container } from '@backstage/ui';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';

/** Display names of notification origins on the settings and notifications pages. */
const originNames = {
  'plugin:announcements': 'Updates & Subscriptions',
};

/**
 * The General tab: profile, appearance, auth tokens, identity and the delete
 * profile card.
 */
export const generalSettingsSubPage = userSettingsPlugin
  .getExtension('sub-page:user-settings/general')
  .override({
    params: {
      loader: () =>
        import('./components/UserSettings/SettingsPage').then(m => (
          <Container>
            <m.GeneralSettingsContent />
          </Container>
        )),
    },
  });

/** The Notifications tab (`/settings/notifications`): notification channels per origin and topic. */
export const notificationsSettingsSubPage = SubPageBlueprint.make({
  name: 'notifications',
  attachTo: { id: 'page:user-settings', input: 'pages' },
  params: {
    path: 'notifications',
    title: 'Notifications',
    loader: () =>
      import('./components/UserNotificationsSetting/UserNotificationsSetting').then(
        m => (
          <Container>
            <m.UserNotificationsSetting originNames={originNames} />
          </Container>
        ),
      ),
  },
});

/**
 * Module for the core `user-settings` plugin: install it next to
 * `@backstage/plugin-user-settings/alpha`. The core feature flags tab is
 * disabled; the Authentication Providers tab stays core, the app attaches its
 * provider list to it.
 */
export const userSettingsModule = createFrontendModule({
  pluginId: 'user-settings',
  extensions: [
    generalSettingsSubPage,
    userSettingsPlugin
      .getExtension('sub-page:user-settings/feature-flags')
      .override({ disabled: true }),
    notificationsSettingsSubPage,
  ],
});

/** The Xelerate notifications page replaces the core one and keeps its path (`/notifications`). */
export const notificationsPage = notificationsPlugin
  .getExtension('page:notifications')
  .override({
    params: {
      loader: () =>
        import('./components/NotificationsPage/NotificationsPage').then(m => (
          <m.NotificationsPage originNames={originNames} markAsReadOnLinkOpen />
        )),
    },
  });

/** The link from the notifications page header to the notification settings. */
export const notificationSettingsHeaderAction =
  PluginHeaderActionBlueprint.make({
    name: 'notification-settings',
    params: {
      loader: async () => (
        <ButtonLink href="/settings/notifications" variant="secondary">
          Notification Settings
        </ButtonLink>
      ),
    },
  });

/**
 * Module for the core `notifications` plugin: install it next to
 * `@backstage/plugin-notifications/alpha`.
 */
export const notificationsOverridesModule = createFrontendModule({
  pluginId: 'notifications',
  extensions: [notificationsPage, notificationSettingsHeaderAction],
});
